import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useCompany } from '@/contexts/CompanyContext';
import { logger } from '@/lib/logger';

const QualityContext = createContext({
    auditCounts: {
        deals: 0,
        calls: 0,
        leads: 0,
        campaigns: 0,
        meetings: 0,
        escalations: 0,
        trackerAlerts: 0 // New: For HubSpot/GDrive combined alerts
    },
    setTrackerAlerts: () => {},
    loading: true
});

export function QualityProvider({ children }) {
    const { currentCompany } = useCompany();
    const [auditCounts, setAuditCounts] = useState({
        deals: 0,
        calls: 0,
        leads: 0,
        campaigns: 0,
        meetings: 0,
        escalations: 0,
        trackerAlerts: 0
    });
    const [loading, setLoading] = useState(true);

    const setTrackerAlerts = (count) => {
        setAuditCounts(prev => ({ ...prev, trackerAlerts: count }));
    };

    useEffect(() => {
        if (!currentCompany?.id) {
            setAuditCounts({
                deals: 0,
                calls: 0,
                leads: 0,
                campaigns: 0,
                meetings: 0,
                escalations: 0,
                trackerAlerts: 0
            });
            setLoading(false);
            return;
        }

        setLoading(true);
        const companyId = currentCompany.id;

        // Helper to fetch counts instead of snapshot (fixes HMR crash)
        const fetchCounts = async () => {
            try {
                const getCount = async (collectionPath, statuses) => {
                    const ref = collection(db, collectionPath);
                    const q = query(ref, where('status', 'in', statuses));
                    const snap = await getDocs(q);
                    return snap.size;
                };

                const [deals, calls, leads, campaigns, meetings] = await Promise.all([
                    getCount(`companies/${companyId}/deal_audits`, ['Pending Audit', 'Pending']),
                    getCount(`companies/${companyId}/call_audits`, ['Pending Audit']),
                    getCount(`companies/${companyId}/lead_audits`, ['Pending Audit']),
                    getCount(`companies/${companyId}/campaign_audits`, ['Pending']),
                    getCount(`companies/${companyId}/meeting_audits`, ['Pending Audit'])
                ]);

                const escRef = collection(db, 'escalation_cases');
                const escQ = query(escRef, where('companyId', '==', companyId), where('status', 'in', ['Open', 'Investigating']));
                const escSnap = await getDocs(escQ);
                const escalations = escSnap.size;

                setAuditCounts(prev => ({
                    ...prev,
                    deals, calls, leads, campaigns, meetings, escalations
                }));
            } catch (err) {
                logger.error('Error fetching quality counts:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchCounts();
        const intervalId = setInterval(fetchCounts, 60000); // Poll every minute

        return () => {
            clearInterval(intervalId);
        };
    }, [currentCompany?.id]);

    return (
        <QualityContext.Provider value={{ auditCounts, loading, setTrackerAlerts }}>
            {children}
        </QualityContext.Provider>
    );
};

export const useQuality = () => useContext(QualityContext);
